import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Tooltip } from '../Tooltip';
import Spinner from '../migratorDashboard/Spinner';
import useFetchOrganizations from '../../hooks/api/useFetchOrganizations';
import useAuth from '../../hooks/auth/useAuth';
import { ui } from '../../config/uiSizes';
import { formatRegion } from '../../utils/formatters';

export type OrgVariant = 'targetOrganization' | 'sourceOrganization';

interface OrganizationSelectorProps {
  orgVariant: OrgVariant;
  value: string;
  onChange: (organizationKey: string) => void;
  disabled?: boolean;
}

export const OrganizationSelector: React.FC<OrganizationSelectorProps> = ({
  orgVariant,
  value,
  onChange,
  disabled
}) => {
  const { organizations, loading: loadingOrgs, fetchOrganizations } = useFetchOrganizations();
  const { accessToken } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (accessToken) {
      fetchOrganizations();
    }
  }, [accessToken, fetchOrganizations]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  // Filter organizations by search term (search both name and region)
  const filteredOrganizations = useMemo(() => {
    if (!searchTerm.trim()) return organizations;

    const searchLower = searchTerm.toLowerCase();
    return organizations.filter((org) => {
      const nameMatch = org.name.toLowerCase().includes(searchLower);
      const regionMatch = org.region?.toLowerCase().includes(searchLower);
      const formattedRegionMatch = formatRegion(org.region).toLowerCase().includes(searchLower);
      return nameMatch || regionMatch || formattedRegionMatch;
    });
  }, [organizations, searchTerm]);

  const getOrganizationKey = (org: { name: string; shortName?: string }): string => {
    const shortName = org.shortName?.trim();
    return shortName && shortName.length > 0 ? shortName : org.name;
  };

  // Find selected organization
  const selectedOrg = organizations.find(
    (org) => getOrganizationKey(org) === value || org.name === value
  );

  // Handle organization selection
  const handleSelectOrg = (organizationKey: string) => {
    onChange(organizationKey);
    setDropdownOpen(false);
    setSearchTerm('');
  };

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent, organizationKey: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleSelectOrg(organizationKey);
    }
  };

  return (
    <>
      <div className={`w-48 flex items-center ${ui.text.formSectionLabel} font-semibold text-gray-800 dark:text-gray-100`}>
        {orgVariant === 'sourceOrganization' ? 'Source Organization:' : 'Target Organization:'}
        <Tooltip
          content={`The ${orgVariant === 'sourceOrganization' ? 'organization where the snapshot data is taken from.' : 'target organization where data will be transferred to.'}`}
          position="right"
        />
      </div>

      <div className="flex-1 space-y-3 relative">
        <button
          type="button"
          ref={buttonRef}
          onClick={() => !disabled && !loadingOrgs && setDropdownOpen(!dropdownOpen)}
          disabled={disabled || loadingOrgs}
          className={`w-full text-left ${ui.text.body} flex items-center justify-between px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed`}
          aria-haspopup="listbox"
          aria-expanded={dropdownOpen}
        >
          <span className={`truncate ${!selectedOrg ? 'text-gray-500 dark:text-gray-400' : ''}`}>
            {loadingOrgs
              ? 'Loading organizations...'
              : selectedOrg
                ? `${selectedOrg.name} - ${formatRegion(selectedOrg.region)}`
                : 'Select an organization'}
          </span>
          <span className="ml-2 flex-shrink-0 text-gray-600 dark:text-gray-400">▾</span>
        </button>

        {dropdownOpen && (
          <div
            ref={dropdownRef}
            className="absolute z-50 top-full mt-1 w-full border border-gray-500 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg rounded-lg overflow-hidden"
            role="listbox"
          >
            {/* Search Input */}
            <div className="p-3 border-b border-gray-200 dark:border-gray-700">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by name or region..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${ui.text.body} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  onClick={(e) => e.stopPropagation()}
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSearchTerm('');
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                    aria-label="Clear search"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {/* Loading State */}
            {loadingOrgs && (
              <div className="flex items-center justify-center gap-3 p-6">
                <Spinner />
                <span className={`${ui.text.body} text-gray-600 dark:text-gray-400`}>Loading organizations...</span>
              </div>
            )}

            {/* Empty State */}
            {!loadingOrgs && filteredOrganizations.length === 0 && (
              <div className="p-6 text-center">
                <span className={`${ui.text.body} text-gray-500 dark:text-gray-400`}>
                  {searchTerm ? 'No organizations match your search' : 'No organizations available'}
                </span>
              </div>
            )}

            {/* Organizations Table */}
            {!loadingOrgs && filteredOrganizations.length > 0 && (
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                    <tr>
                      <th className={`text-left px-4 py-3 ${ui.text.tableHeader} font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600`}>
                        Organization Name
                      </th>
                      <th className={`text-left px-4 py-3 ${ui.text.tableHeader} font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600`}>
                        Region
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrganizations.map((org) => {
                      const orgKey = getOrganizationKey(org);
                      const isSelected = orgKey === value || org.name === value;
                      return (
                        <tr
                          key={org.id}
                          onClick={() => handleSelectOrg(orgKey)}
                          onKeyDown={(e) => handleKeyDown(e, orgKey)}
                          tabIndex={0}
                          role="option"
                          aria-selected={isSelected}
                          className={`cursor-pointer transition-colors ${isSelected
                            ? 'bg-blue-50 dark:bg-blue-900/30'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                            }`}
                        >
                          <td className={`px-4 py-3 ${ui.text.tableCell} text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700`}>
                            <div className="flex items-center gap-2">
                              {isSelected && (
                                <svg className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                              <span className={isSelected ? 'font-semibold' : ''}>{org.name}</span>
                            </div>
                          </td>
                          <td className={`px-4 py-3 ${ui.text.tableCell} text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700`}>
                            {formatRegion(org.region)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default OrganizationSelector;
